import { TextArea, TextInput, WithErrorForm } from "components/common";
import { Modal, ModalProps } from "components/common/Modal";
import { Button } from "components/parts/Button/Button";
import { Typography } from "components/parts/Typography/Typography";
import { useFormik } from "formik";
import { useConsoleState } from "providers/ConsoleStateProvider";
import { useLoadingState } from "providers/LoadingProvider";
import React from "react";
import { useHistory } from "react-router";
import {
  CreateGroupInput,
  DeleteGroupInput,
  Group,
  UpdateGroupInput,
} from "types/graphqlapi";
import {
  createGroupMutation,
  deleteGroupMutation,
  updateGroupMutation,
} from "utils/apis/group";
import { validationSchemaGroup } from "utils/validation";

import {
  StyledButtonArea,
  StyledForm,
  StyledFormRow,
  StyledModalContent,
  StyledModalFooter,
  StyledModalHeader,
} from "./styled";

type GroupManageModalProps = {
  selectedGroup: Group;
  clearSelectedGroup: () => void;
} & ModalProps;

type GroupFormValueType = {
  name: string;
  memo: string;
};

export const GroupManageModal: React.FC<GroupManageModalProps> = ({
  closeHandler,
  selectedGroup,
  clearSelectedGroup,
}) => {
  const history = useHistory();
  const { setLoading } = useLoadingState();
  const { userId } = useConsoleState();
  const insertGroup = async (values: GroupFormValueType): Promise<void> => {
    try {
      const createSubjectInput: CreateGroupInput = {
        name: values.name,
        memo: values.memo,
      };
      await createGroupMutation(createSubjectInput, userId);
      clearSelectedGroup();
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      setLoading(false);
      history.replace("/auth/login");
    }
  };

  const updateGroup = async (values: GroupFormValueType): Promise<void> => {
    try {
      const updateSubjectInput: UpdateGroupInput = {
        id: selectedGroup.id,
        name: values.name,
        memo: values.memo,
      };
      await updateGroupMutation(updateSubjectInput, userId);
      clearSelectedGroup();
    } catch (e) {
      console.error(e);
      alert("エラーが発生しました。");
      setLoading(false);
      history.replace("/auth/login");
    }
  };

  const clickDeleteButtonHandler: React.FormEventHandler<
    HTMLButtonElement
  > = async () => {
    const res = confirm("選択されたグループを削除します");
    if (res) {
      try {
        setLoading(true);
        const deleteGroupInput: DeleteGroupInput = {
          id: selectedGroup.id,
        };
        await deleteGroupMutation(deleteGroupInput, userId);
        // TOTO カスケードしての削除
        clearSelectedGroup();
      } catch (e) {
        console.error(e);
        alert("エラーが発生しました。");
        setLoading(false);
        history.replace("/auth/login");
      }
    }
  };

  const onSubmit = (values: GroupFormValueType) => {
    setLoading(true);
    selectedGroup.id ? updateGroup(values) : insertGroup(values);
  };

  const formik = useFormik<GroupFormValueType>({
    initialValues: {
      name: selectedGroup?.name || "",
      memo: selectedGroup?.memo || "",
    },
    validationSchema: validationSchemaGroup,
    onSubmit: onSubmit,
  });

  return (
    <Modal closeHandler={closeHandler}>
      <StyledForm onSubmit={formik.handleSubmit}>
        <StyledModalHeader>
          {selectedGroup.id ? (
            <Typography text="グループ編集" size="large" />
          ) : (
            <Typography text="新規グループ登録" size="large" />
          )}
        </StyledModalHeader>
        <StyledModalContent>
          <StyledFormRow>
            <Typography text="グループ名" required width="7em" />
            <WithErrorForm
              touched={formik.touched.name}
              errorMessage={formik.errors.name}
            >
              <TextInput
                autoFocus
                {...formik.getFieldProps("name")}
                width="16rem"
                maxlength="30"
              />
            </WithErrorForm>
          </StyledFormRow>
          <StyledFormRow>
            <Typography text="備考" width="7em" />
            <WithErrorForm
              touched={formik.touched.memo}
              errorMessage={formik.errors.memo}
            >
              <TextArea
                {...formik.getFieldProps("memo")}
                width="20rem"
                maxlength="150"
              />
            </WithErrorForm>
          </StyledFormRow>
        </StyledModalContent>
        <StyledModalFooter>
          <StyledButtonArea>
            {selectedGroup.id && (
              <Button
                onClick={clickDeleteButtonHandler}
                variant="secondary"
                text="削除"
                type="button"
              />
            )}
            <Button text={selectedGroup.id ? "更新" : "登録"} type="submit" />
          </StyledButtonArea>
        </StyledModalFooter>
      </StyledForm>
    </Modal>
  );
};
